import React, { useRef, useEffect, useState } from 'react';
import {
  SiAndroid, SiKotlin, SiSwift, SiReact, SiJetpackcompose,
  SiOpenai, SiLangchain, SiHuggingface,
  SiLaravel, SiSpringboot, SiPython, SiFlask, SiPhp, SiShopify,
  SiDocker, SiLinux, SiSnowflake, SiGit, SiGithub,
  SiFigma, SiFirebase, SiGooglegemini, SiGithubactions,
  SiTensorflow, SiOpencv, SiIos,
} from 'react-icons/si';
import { FaJava, FaAws, FaRobot, FaCloud, FaMobileAlt } from 'react-icons/fa';
import { FiServer, FiCpu, FiShoppingBag, FiSettings } from 'react-icons/fi';
import { trackSectionView } from '../utils/mixpanel';
import '../assets/styles/Skills.scss';

interface Skill {
  name: string;
  icon: React.ReactNode;
}

const row1: Skill[] = [
  { name: 'Android', icon: <SiAndroid /> },
  { name: 'Kotlin', icon: <SiKotlin /> },
  { name: 'Swift', icon: <SiSwift /> },
  { name: 'iOS', icon: <SiIos /> },
  { name: 'React Native', icon: <SiReact /> },
  { name: 'Jetpack Compose', icon: <SiJetpackcompose /> },
  { name: 'Java', icon: <FaJava /> },
  { name: 'Firebase', icon: <SiFirebase /> },
  { name: 'Git', icon: <SiGit /> },
  { name: 'GitHub', icon: <SiGithub /> },
];

const row2: Skill[] = [
  { name: 'OpenAI', icon: <SiOpenai /> },
  { name: 'Gemini', icon: <SiGooglegemini /> },
  { name: 'LangChain', icon: <SiLangchain /> },
  { name: 'Hugging Face', icon: <SiHuggingface /> },
  { name: 'TensorFlow', icon: <SiTensorflow /> },
  { name: 'Computer Vision', icon: <SiOpencv /> },
  { name: 'AI Agents', icon: <FaRobot /> },
  { name: 'On-Device AI', icon: <FaRobot /> },
];

const row3: Skill[] = [
  { name: 'Laravel', icon: <SiLaravel /> },
  { name: 'Spring Boot', icon: <SiSpringboot /> },
  { name: 'Python', icon: <SiPython /> },
  { name: 'Flask', icon: <SiFlask /> },
  { name: 'PHP', icon: <SiPhp /> },
  { name: 'Shopify', icon: <SiShopify /> },
  { name: 'Docker', icon: <SiDocker /> },
  { name: 'AWS', icon: <FaAws /> },
  { name: 'Azure', icon: <FaCloud /> },
  { name: 'Linux', icon: <SiLinux /> },
  { name: 'Snowflake', icon: <SiSnowflake /> },
  { name: 'GitHub Actions', icon: <SiGithubactions /> },
  { name: 'Figma', icon: <SiFigma /> },
];

const SCROLL_SPEED = 0.8; // px per 16ms frame
const RESUME_DELAY = 1200;

const SkillPill = ({ skill }: { skill: Skill }) => (
  <div className="skill-pill">
    <span className="skill-pill__icon">{skill.icon}</span>
    <span className="skill-pill__name">{skill.name}</span>
  </div>
);

const SkillRow = ({ skills, direction }: { skills: Skill[]; direction: 'left' | 'right' }) => {
  // 4 copies so even short rows always overflow the viewport
  const copies = [...skills, ...skills, ...skills, ...skills];
  const trackRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const posRef = useRef(0);
  const isInteracting = useRef(false);
  const animFrameId = useRef(0);
  const resumeTimerId = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Touch drag state
  const touchStartX = useRef(0);
  const touchStartPos = useRef(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    // Wait one frame for layout so offsetWidth is correct
    const initFrame = requestAnimationFrame(() => {
      const oneCopyWidth = track.offsetWidth / 4;
      // Start in the middle (2 copies in) so both directions have room
      posRef.current = direction === 'right' ? -oneCopyWidth * 2 : -oneCopyWidth;
      track.style.transform = `translateX(${posRef.current}px)`;
    });

    let lastTs: number | null = null;

    const tick = (ts: number) => {
      if (!isInteracting.current) {
        const elapsed = lastTs !== null ? ts - lastTs : 16;
        const delta = SCROLL_SPEED * (elapsed / 16);
        const oneCopyWidth = track.offsetWidth / 4;

        if (direction === 'left') {
          posRef.current -= delta;
          // Seamless loop: jumped past the first copy, reset by one copy width
          if (posRef.current <= -oneCopyWidth * 2) {
            posRef.current += oneCopyWidth;
          }
        } else {
          posRef.current += delta;
          // Seamless loop: scrolled back past start of second copy
          if (posRef.current >= -oneCopyWidth) {
            posRef.current -= oneCopyWidth;
          }
        }

        track.style.transform = `translateX(${posRef.current}px)`;
        lastTs = ts;
      } else {
        lastTs = null;
      }
      animFrameId.current = requestAnimationFrame(tick);
    };

    animFrameId.current = requestAnimationFrame(tick);

    // Touch: pause auto-scroll while finger is on the row,
    // also allow manual swipe via translateX
    const handleTouchStart = (e: TouchEvent) => {
      isInteracting.current = true;
      touchStartX.current = e.touches[0].clientX;
      touchStartPos.current = posRef.current;
      if (resumeTimerId.current) clearTimeout(resumeTimerId.current);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const dx = e.touches[0].clientX - touchStartX.current;
      posRef.current = touchStartPos.current + dx;
      track.style.transform = `translateX(${posRef.current}px)`;
    };

    const handleTouchEnd = () => {
      resumeTimerId.current = setTimeout(() => {
        isInteracting.current = false;
      }, RESUME_DELAY);
    };

    const wrapper = wrapperRef.current;
    wrapper?.addEventListener('touchstart', handleTouchStart, { passive: true });
    wrapper?.addEventListener('touchmove', handleTouchMove, { passive: true });
    wrapper?.addEventListener('touchend', handleTouchEnd, { passive: true });
    wrapper?.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      cancelAnimationFrame(initFrame);
      cancelAnimationFrame(animFrameId.current);
      if (resumeTimerId.current) clearTimeout(resumeTimerId.current);
      wrapper?.removeEventListener('touchstart', handleTouchStart);
      wrapper?.removeEventListener('touchmove', handleTouchMove);
      wrapper?.removeEventListener('touchend', handleTouchEnd);
      wrapper?.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [direction]);

  // Desktop mouse drag
  const isDragging = useRef(false);
  const mouseStartX = useRef(0);
  const mouseDragStartPos = useRef(0);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') return;
    isDragging.current = true;
    isInteracting.current = true;
    mouseStartX.current = e.clientX;
    mouseDragStartPos.current = posRef.current;
    wrapperRef.current?.setPointerCapture(e.pointerId);
    if (resumeTimerId.current) clearTimeout(resumeTimerId.current);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || !isDragging.current || !trackRef.current) return;
    const dx = e.clientX - mouseStartX.current;
    posRef.current = mouseDragStartPos.current + dx;
    trackRef.current.style.transform = `translateX(${posRef.current}px)`;
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || !isDragging.current) return;
    isDragging.current = false;
    resumeTimerId.current = setTimeout(() => { isInteracting.current = false; }, RESUME_DELAY);
  };

  return (
    <div
      className="skill-marquee-row"
      ref={wrapperRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      <div className="skill-marquee-track" ref={trackRef}>
        {copies.map((skill, index) => (
          <SkillPill key={`${skill.name}-${index}`} skill={skill} />
        ))}
      </div>
    </div>
  );
};

const broadSkills = [
  { label: 'Backend', icon: <FiServer /> },
  { label: 'Shopify', icon: <FiShoppingBag /> },
  { label: 'AI / LLM', icon: <FiCpu /> },
  { label: 'DevOps', icon: <FiSettings /> },
];

const deepSkill = {
  label: 'Mobile Apps',
  sub: ['Android', 'iOS', 'React Native', 'KMP'],
};

function useCountUp(target: number, duration: number = 1500) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const startTime = performance.now();
          const tick = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return { count, ref };
}

const TShape = () => {
  const { count, ref } = useCountUp(8, 1200);

  return (
    <div className="t-shape" ref={ref}>
      {/* Label */}
      <div className="t-shape__section-label">Broad Knowledge</div>

      {/* Node rail — top of the T */}
      <div className="t-shape__rail">
        {broadSkills.map((item) => (
          <div key={item.label} className="t-shape__node">
            <div className="t-shape__node-card">
              <span className="t-shape__node-icon">{item.icon}</span>
              <span className="t-shape__node-name">{item.label}</span>
            </div>
            <div className="t-shape__node-tick" />
          </div>
        ))}
      </div>

      {/* Arrow connector */}
      <div className="t-shape__connector" />

      {/* Stem — deep expertise */}
      <div className="t-shape__stem">
        <div className="t-shape__depth-label"><span>Depth</span></div>
        <div className="t-shape__stem-label">Deep Expertise</div>
        <div className="t-shape__vertical-title">{deepSkill.label}</div>
        <div className="t-shape__divider" />
        <div className="t-shape__subs">
          {deepSkill.sub.map((s) => (
            <span key={s} className="t-shape__sub-item">{s}</span>
          ))}
        </div>
      </div>

      {/* Years stat — outside below stem */}
      <div className="t-shape__years">{count}+ years of experience</div>
    </div>
  );
};

const depthBars = [
  { label: 'Mobile Apps',  level: 'Expert',     fill: 95, icon: <FaMobileAlt /> },
  { label: 'Backend',      level: 'Strong',      fill: 82, icon: <FiServer />    },
  { label: 'Shopify Apps', level: 'Proficient',  fill: 70, icon: <FiShoppingBag />},
  { label: 'AI / LLM',     level: 'Proficient',  fill: 65, icon: <FiCpu />       },
  { label: 'DevOps',       level: 'Advancing',   fill: 50, icon: <FiSettings />  },
];

const levelColor: Record<string, string> = {
  Expert:     '#3b82f6',
  Strong:     '#3b82f6',
  Proficient: '#3b82f6',
  Advancing:  '#3b82f6',
};

function DepthBars() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="depth-bars" ref={ref}>
      <div className="depth-bars__header">
        <span className="depth-bars__title">Skill Depth</span>
      </div>
      {depthBars.map((bar) => (
        <div key={bar.label} className="depth-bar">
          <div className="depth-bar__meta">
            <span className="depth-bar__icon">{bar.icon}</span>
            <span className="depth-bar__label">{bar.label}</span>
            <span className="depth-bar__level">{bar.level}</span>
          </div>
          <div className="depth-bar__track">
            <div
              className="depth-bar__fill"
              style={{ width: visible ? `${bar.fill}%` : '0%' }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function Skills() {
  useEffect(() => {
    trackSectionView('skills');
  }, []);

  return (
    <section className="skills-section" id="skills">
      <div className="skills-section__body">
        {/* Left: Header + Marquee rows + Depth bars */}
        <div className="skills-section__left">
          <div className="skills-section__left-header">
            <h2>Tech Stack</h2>
            <p className="skills-section__subtitle">My T-shaped expertise — deep in mobile, broad across the stack.</p>
          </div>
          <DepthBars />
          <div className="skills-section__marquee-wrap">
            <div className="skills-marquee-container">
              <SkillRow skills={row1} direction="left" />
              <SkillRow skills={row2} direction="right" />
              <SkillRow skills={row3} direction="left" />
            </div>
            <div className="skills-section__fade skills-section__fade--left" />
            <div className="skills-section__fade skills-section__fade--right" />
          </div>
        </div>

        {/* Right: T-Shape */}
        <div className="skills-section__right">
          <TShape />
        </div>
      </div>
    </section>
  );
}

export default Skills;
